import React, { useState } from "react";
import { Colors } from "../../Constants/colors";
import { FontFamily } from "../../Constants/Enums/FontFamily.enums";
import {
  ALIGN_ITEMS,
  DISPLAY,
  FONT_SIZE,
  FONT_WEIGHT,
  SPACING,
} from "../../Constants/Enums/styles.enum";
import { SelectedParents as SelectedParentsInterface } from "../../Redux/Slices/studentAdmission.slice";

interface Props {
  selectedParents?: SelectedParentsInterface;
  onParentsChange: () => unknown;
}

export const SelectedParents = ({
  selectedParents,
  onParentsChange,
}: Props) => {
  const [isChangeHovered, setIsChangeHovered] = useState(false);

  return (
    <div
      style={{
        backgroundColor: `${Colors.VeryLightGrey}30`,
        borderRadius: "12px",
        padding: SPACING.S24,
        border: `1px solid ${Colors.LightGrey}`,
      }}
    >
      {/* Header Message */}
      <div
        style={{
          fontFamily: FontFamily.ROBOTO_REGULAR,
          fontSize: FONT_SIZE.F14,
          color: Colors.Grey,
          lineHeight: "1.6",
          marginBottom: SPACING.S20,
        }}
      >
        You have chosen the following parent. If you need to make changes,
        please{" "}
        <span
          style={{
            color: isChangeHovered ? Colors.TangerineOrange : Colors.AshRose,
            cursor: "pointer",
            textDecoration: "underline",
            fontWeight: FONT_WEIGHT.MEDIUM,
            transition: "color 0.2s ease",
          }}
          onClick={onParentsChange}
          onMouseEnter={() => setIsChangeHovered(true)}
          onMouseLeave={() => setIsChangeHovered(false)}
        >
          select another parent
        </span>
        .
      </div>

      <div
        style={{
          backgroundColor: Colors.White,
          borderRadius: "8px",
          padding: SPACING.S24,
          boxShadow: "0 2px 8px rgba(0, 0, 0, 0.04)",
        }}
      >
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(2, 1fr)",
            gap: SPACING.S20,
          }}
        >
          <ParentsDetail
            title="Full Name"
            value={selectedParents?.name || "Not provided"}
            icon={
              <svg
                width="18"
                height="18"
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z"
                  fill={Colors.TangerineOrange}
                />
              </svg>
            }
          />
          <ParentsDetail
            title="Relationship"
            value={selectedParents?.parentalRelationship || "Not specified"}
            icon={
              <svg
                width="18"
                height="18"
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M16.5 3c-1.74 0-3.41.81-4.5 2.09C10.91 3.81 9.24 3 7.5 3 4.42 3 2 5.42 2 8.5c0 3.78 3.4 6.86 8.55 11.54L12 21.35l1.45-1.32C18.6 15.36 22 12.28 22 8.5 22 5.42 19.58 3 16.5 3z"
                  fill={Colors.TangerineOrange}
                />
              </svg>
            }
          />
          <ParentsDetail
            title="Mobile Number"
            value={selectedParents?.mobile || "Not provided"}
            icon={
              <svg
                width="18"
                height="18"
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M17 1.01L7 1c-1.1 0-2 .9-2 2v18c0 1.1.9 2 2 2h10c1.1 0 2-.9 2-2V3c0-1.1-.9-1.99-2-1.99zM17 19H7V5h10v14z"
                  fill={Colors.TangerineOrange}
                />
              </svg>
            }
          />
          <ParentsDetail
            title="Address"
            value={selectedParents?.address || "Not provided"}
            icon={
              <svg
                width="18"
                height="18"
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z"
                  fill={Colors.TangerineOrange}
                />
              </svg>
            }
          />
        </div>
      </div>
    </div>
  );
};

const ParentsDetail = ({
  title,
  value,
  icon,
}: {
  title: string;
  value: string;
  icon: React.ReactNode;
}) => {
  return (
    <div
      style={{
        display: DISPLAY.FLEX,
        gap: SPACING.S12,
        alignItems: ALIGN_ITEMS.START,
      }}
    >
      <div
        style={{
          marginTop: SPACING.S2,
          flexShrink: 0,
        }}
      >
        {icon}
      </div>

      {/* Content */}
      <div style={{ flex: 1 }}>
        <div
          style={{
            fontFamily: FontFamily.POPPINS_REGULAR,
            fontSize: "11px",
            color: Colors.Grey,
            textTransform: "uppercase",
            letterSpacing: "0.5px",
            marginBottom: SPACING.S4,
            fontWeight: 500,
          }}
        >
          {title}
        </div>
        <div
          style={{
            fontFamily: FontFamily.ROBOTO_REGULAR,
            fontSize: FONT_SIZE.F14,
            color: Colors.DarkGrey,
            fontWeight: FONT_WEIGHT.REGULAR,
            lineHeight: "1.5",
            wordBreak: "break-word",
          }}
        >
          {value}
        </div>
      </div>
    </div>
  );
};
